<template>
  <div class="container-fluid search-page rfo-page rfo-page--standard oa-workspace">
    <OdooPaymentHeader />
    <OdooPaymentProgress :steps="stepsMeta" :active-step="activeStep" />

    <!-- Order context strip (visible once we have a UUID) -->
    <OrderContextPanel
      v-if="hasContext"
      :uuid="uuid"
      :chips="contextChips"
      :state="stateBadge"
      @copy="copyUuid"
      @reset="resetToLookup"
    />

    <!-- State A: Lookup -->
    <OrderLookupCard
      v-if="phase === 'lookup'"
      :uuid="uuid"
      :is-looking="isLooking"
      @lookup="onLookup"
    />

    <!-- State B: Trigger + Wait -->
    <TriggerAndWaitCard
      v-if="phase === 'awaiting-queue-message'"
      :uuid="uuid"
      :seen-rejection-log-id="recovery?.sourceLogId ?? null"
      @found="onQueueMessageFound"
      @rejected="onRejectionDetected"
      @cancelled="onTriggerCancelled"
      @audit="refreshAuditLogs"
    />

    <!-- State B': Odoo refused, and the payload cannot be reused -->
    <RejectedRequestCard
      v-if="phase === 'rejected' && recovery"
      :recovery="recovery"
      :uuid="uuid"
      @restart="resetToLookup"
    />

    <!-- State C: Fill payment -->
    <template v-if="queueMessage && phase === 'fill-payment'">
      <RecoveredBodyBanner v-if="recovery" class="mt-4" :recovery="recovery" />

      <div class="card card-absolute rfo-card rfo-card--primary oa-section-card">
        <div class="card-header bg-primary text-white d-flex align-items-center">
          <h5 class="mb-0"><i class="fa fa-credit-card me-2"></i>Payment Details</h5>
        </div>
        <div class="card-body">
          <PaymentObjectForm
            ref="paymentFormRef"
            v-model="paymentDraft"
            :expected-amount-total="queueMessage.body?.amount_total_ryd ?? null"
          />
        </div>
      </div>

      <div class="oa-json-disclosure">
        <button
          type="button"
          class="oa-json-disclosure__toggle"
          :aria-expanded="showJsonPreview"
          aria-controls="oa-json-preview-body"
          @click="showJsonPreview = !showJsonPreview"
        >
          <i class="fa fa-code oa-json-disclosure__icon" aria-hidden="true"></i>
          <span class="oa-json-disclosure__label">
            Show Or Edit The Full Data That Will Be Sent To Odoo
          </span>
          <span class="oa-json-disclosure__hint">
            ({{ showJsonPreview ? 'hide' : 'technical, optional' }})
          </span>
          <span v-if="isBodyEdited" class="badge bg-warning text-dark ms-2">Edited</span>
          <i
            class="fa oa-json-disclosure__chev"
            :class="showJsonPreview ? 'fa-chevron-up' : 'fa-chevron-down'"
            aria-hidden="true"
          ></i>
        </button>
        <div v-show="showJsonPreview" id="oa-json-preview-body" class="oa-json-disclosure__body">
          <div class="alert alert-warning outline small oa-json-disclosure__warning">
            <i class="fa fa-exclamation-triangle me-1"></i>
            Anything you change here is sent to Odoo exactly as written and creates a real invoice.
            The payment fields above stay in charge of <code>payment</code>; everything else is
            yours to edit.
          </div>
          <textarea
            v-model="bodyDraftText"
            class="form-control border border-primary font-monospace small oa-json-preview"
            :class="{ 'is-invalid': !!bodyDraftError }"
            rows="14"
            spellcheck="false"
            aria-label="Full data that will be sent to Odoo"
            @blur="onBodyDraftBlur"
          ></textarea>
          <div v-if="bodyDraftError" class="alert alert-danger outline small mt-2 mb-0">
            <i class="fa fa-exclamation-circle me-1"></i>{{ bodyDraftError }}
          </div>
          <div class="d-flex justify-content-end mt-2">
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              :disabled="!isBodyEdited && !bodyDraftError"
              @click="resetBodyDraft()"
            >
              <i class="fa fa-undo me-1"></i>Reset To {{ recovery ? 'Recovered' : 'Original' }} Body
            </button>
          </div>
        </div>
      </div>

      <PaymentCommitPanel
        v-model:acknowledge-overwrite="acknowledgeOverwrite"
        :has-existing-payment="hasExistingPayment"
        :existing-payment-status="existingPaymentStatus"
        :submit-error="submitError"
        :submit-blocker-message="submitBlockerMessage"
        :is-submitting="isSubmitting"
        :can-submit="canSubmit"
        @cancel="resetToLookup"
        @submit="onSubmit"
      />
    </template>

    <!-- State D: Result -->
    <ResultCard
      v-if="phase === 'result'"
      :success="submitSuccess"
      :message="submitError"
      :uuid="uuid"
      :previous-queue-message-id="previousQueueMessageId"
      @restart="resetToLookup"
      @back="phase = 'fill-payment'"
    />

    <OdooPaymentAuditLogsCard ref="auditLogsCardRef" :uuid="uuid" />

    <PayOpsUsageInstructions
      class="mt-4"
      description="Attach a payment object to an Odoo queue.message so the invoice can finish in Odoo."
      :required-roles="odooPaymentRequiredRoles"
      :workflow-notes="odooPaymentWorkflowNotes"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { newAuth } from '@/firebase/firebaseInit';
import { apiClient, API_PATHS } from '@/services/api';
import { copyToClipboard, formatCurrency } from '@/utils/display-formatters';
import OrderLookupCard from './components/OrderLookupCard.vue';
import TriggerAndWaitCard from './components/TriggerAndWaitCard.vue';
import PaymentObjectForm from './components/PaymentObjectForm.vue';
import ResultCard from './components/ResultCard.vue';
import OdooPaymentHeader from './components/OdooPaymentHeader.vue';
import OdooPaymentProgress from './components/OdooPaymentProgress.vue';
import OrderContextPanel from './components/OrderContextPanel.vue';
import PaymentCommitPanel from './components/PaymentCommitPanel.vue';
import OdooPaymentAuditLogsCard from './components/OdooPaymentAuditLogsCard.vue';
import RecoveredBodyBanner from './components/RecoveredBodyBanner.vue';
import RejectedRequestCard from './components/RejectedRequestCard.vue';
import PayOpsUsageInstructions from '../shared/PayOpsUsageInstructions.vue';
import {
  PAY_OPS_ODOO_PAYMENT_ROLES,
  buildOdooPaymentWorkflowNotes,
} from '@/services/ryd-pay-operations/order-amendments/usage-instructions-config';
import {
  mergePaymentIntoBody,
  parseBodyDraft,
  resolveLookupOutcome,
} from '@/services/ryd-pay-operations/order-amendments/odoo-payment-recovery';

const toast = useToast();

const stepsMeta = [
  { key: 'lookup', title: 'Find The Order', hint: 'Enter the order ID', icon: 'fa-search' },
  {
    key: 'trigger',
    title: 'Prepare Invoice',
    hint: 'Wait for Odoo to be ready',
    icon: 'fa-bolt',
  },
  {
    key: 'fill',
    title: 'Add Payment',
    hint: 'Enter the payment details',
    icon: 'fa-credit-card',
  },
  { key: 'result', title: 'Verify', hint: 'Confirm Odoo processed it', icon: 'fa-check-circle' },
];

const odooPaymentRequiredRoles = PAY_OPS_ODOO_PAYMENT_ROLES;
const odooPaymentWorkflowNotes = buildOdooPaymentWorkflowNotes();

const uuid = ref('');
const isLooking = ref(false);
// 'lookup' | 'awaiting-queue-message' | 'rejected' | 'fill-payment' | 'result'
const phase = ref('lookup');
const queueMessage = ref(null);
/** Set when the body came from a request Odoo rejected rather than a stored queue.message. */
const recovery = ref(null);
const paymentDraft = ref(null);
const paymentFormRef = ref(null);
const auditLogsCardRef = ref(null);
const acknowledgeOverwrite = ref(false);
const isSubmitting = ref(false);
const submitSuccess = ref(false);
const submitError = ref('');
const showJsonPreview = ref(false);
/** Textarea contents. Diverges from `previewJson` only while the operator is typing. */
const bodyDraftText = ref('');
const bodyDraftError = ref('');
/**
 * Operator-owned body. Null until the raw editor is used; from then on it is the base for
 * submit, and the payment form rewrites only its `payment` key so other edits survive.
 */
const bodyDraftBase = ref(null);
// Captured at submit time so the result step can distinguish the freshly-created
// queue.message from any stale one Odoo holds for this order.
const previousQueueMessageId = ref(null);

const activeStep = computed(() => {
  switch (phase.value) {
    case 'lookup':
      return 0;
    case 'awaiting-queue-message':
    case 'rejected':
      return 1;
    case 'fill-payment':
      return 2;
    case 'result':
      return 3;
    default:
      return 0;
  }
});

const hasContext = computed(() => phase.value !== 'lookup' && !!uuid.value);

const contextChips = computed(() => {
  const out = [];
  if (queueMessage.value?.body?.partner_id?.name) {
    out.push({ label: 'Partner', value: queueMessage.value.body.partner_id.name });
  }
  const total = queueMessage.value?.body?.amount_total_ryd;
  const currency = queueMessage.value?.body?.currency;
  if (total != null) {
    out.push({ label: 'Total', value: formatCurrency(total, currency || 'EUR') });
  }
  if (queueMessage.value?.body?.point_of_interest_id?.name) {
    out.push({ label: 'POI', value: queueMessage.value.body.point_of_interest_id.name });
  }
  if (queueMessage.value?.id != null) {
    out.push({ label: 'Odoo record', value: `#${queueMessage.value.id}` });
  }
  return out;
});

const stateBadge = computed(() => {
  switch (phase.value) {
    case 'awaiting-queue-message':
      return { tone: 'warning', icon: 'fa-clock-o', label: 'Waiting for Odoo' };
    case 'rejected':
      return { tone: 'danger', icon: 'fa-ban', label: 'Odoo refused' };
    case 'fill-payment':
      return { tone: 'primary', icon: 'fa-pencil', label: 'Ready to send' };
    case 'result':
      return submitSuccess.value
        ? { tone: 'success', icon: 'fa-check', label: 'Payment sent' }
        : { tone: 'danger', icon: 'fa-exclamation-triangle', label: 'Send failed' };
    default:
      return { tone: 'secondary', icon: 'fa-circle-o', label: 'Looking up' };
  }
});

const hasExistingPayment = computed(() => {
  const payment = queueMessage.value?.body?.payment;
  return payment && typeof payment === 'object' && Object.keys(payment).length > 0;
});

const existingPaymentStatus = computed(
  () => queueMessage.value?.body?.payment?.status ?? 'unknown',
);

const isBodyEdited = computed(() => bodyDraftBase.value !== null);

/**
 * The body that will actually be sent: the operator's edited body when the raw editor has been
 * used, otherwise the looked-up or recovered one — with `payment` always owned by the form.
 */
const effectiveBody = computed(() =>
  mergePaymentIntoBody(
    bodyDraftBase.value ?? queueMessage.value?.body ?? {},
    buildPaymentForApi(paymentDraft.value) ?? {},
  ),
);

const previewJson = computed(() => {
  if (!queueMessage.value) return '';
  // Shows the ISO-formatted date — that's what we actually send.
  const preview = {
    format: queueMessage.value.format ?? true,
    model: queueMessage.value.model ?? 'sale.order',
    body: effectiveBody.value,
  };
  return JSON.stringify(preview, null, 2);
});

// Re-render the editor whenever the payload changes for a reason other than typing. Typing does
// not feed `previewJson`, so an in-progress edit is never clobbered; a blur that fails to parse
// leaves `bodyDraftBase` untouched and the operator keeps their broken text to fix.
watch(previewJson, (next) => {
  bodyDraftText.value = next;
});

const canSubmit = computed(() => {
  if (isSubmitting.value) return false;
  if (bodyDraftError.value) return false;
  if (!paymentFormRef.value?.isValid) return false;
  if (hasExistingPayment.value && !acknowledgeOverwrite.value) return false;
  return true;
});

const submitBlockerMessage = computed(() => {
  if (bodyDraftError.value) return 'Fix the edited data before sending.';
  if (!paymentFormRef.value?.isValid) return 'Fill in the required payment fields before sending.';
  if (hasExistingPayment.value && !acknowledgeOverwrite.value)
    return 'Confirm the replacement before sending.';
  return '';
});

function onBodyDraftBlur() {
  const { body: envelope, error } = parseBodyDraft(bodyDraftText.value);
  if (error) {
    bodyDraftError.value = error;
    return;
  }
  const nextBody = envelope.body;
  if (!nextBody || typeof nextBody !== 'object' || Array.isArray(nextBody)) {
    bodyDraftError.value = 'The data must contain a "body" object.';
    return;
  }
  bodyDraftError.value = '';
  bodyDraftBase.value = nextBody;
}

function resetBodyDraft() {
  bodyDraftBase.value = null;
  bodyDraftError.value = '';
  bodyDraftText.value = previewJson.value;
}

async function onLookup(nextUuid) {
  uuid.value = nextUuid;
  isLooking.value = true;
  submitError.value = '';
  queueMessage.value = null;
  recovery.value = null;
  acknowledgeOverwrite.value = false;
  resetBodyDraft();
  try {
    const { data } = await apiClient.rpIntegration.get(
      API_PATHS.rpIntegration.invoiceCancellationModification.queueMessage(nextUuid),
    );
    const existing = data && data.id ? data : null;
    // Only ask Odoo about rejected requests when there is no queue.message to work from.
    const rejected = existing ? null : await fetchRecovery(nextUuid);
    applyLookupOutcome(resolveLookupOutcome(existing, rejected));
  } catch (err) {
    submitError.value = err?.response?.data?.message ?? err?.message ?? 'Lookup failed.';
    toast.error(submitError.value);
  } finally {
    isLooking.value = false;
  }
}

/**
 * A failed recovery check must never dead-end the operator — returning null routes them to the
 * trigger card, which is still a valid next step.
 */
async function fetchRecovery(nextUuid) {
  try {
    const res = await apiClient.rpIntegration.get(
      API_PATHS.rpIntegration.odooSaleOrder.rejectedQueueMessageBody(nextUuid),
    );
    return res.data?.data ?? null;
  } catch (err) {
    console.warn('Rejected queue.message recovery lookup failed', err);
    return null;
  }
}

function applyLookupOutcome(outcome) {
  switch (outcome.kind) {
    case 'existing':
    case 'recovered':
      queueMessage.value = outcome.queueMessage;
      recovery.value = outcome.recovery ?? null;
      seedPaymentDraft(outcome.queueMessage);
      resetBodyDraft();
      phase.value = 'fill-payment';
      break;
    case 'rejected':
      recovery.value = outcome.recovery;
      phase.value = 'rejected';
      break;
    default:
      phase.value = 'awaiting-queue-message';
  }
}

function onQueueMessageFound(message) {
  queueMessage.value = message;
  recovery.value = null;
  seedPaymentDraft(message);
  resetBodyDraft();
  phase.value = 'fill-payment';
  auditLogsCardRef.value?.refresh?.();
}

/** Odoo refused the request we just triggered — route straight into recovery. */
function onRejectionDetected(rejected) {
  applyLookupOutcome(resolveLookupOutcome(null, rejected));
  auditLogsCardRef.value?.refresh?.();
}

function onTriggerCancelled() {
  phase.value = 'lookup';
}

function refreshAuditLogs() {
  auditLogsCardRef.value?.refresh?.();
}

function seedPaymentDraft(message) {
  // operation_type is intentionally NOT carried over — Odoo decides it based
  // on the order's current invoicing state (e.g. CREATE_ORDER vs CORRECT_ORDER
  // for an already-invoiced order). Forcing a value here can produce errors
  // like "Cannot update after invoicing. Use CORRECT_ORDER operation type instead."
  const existing = message.body?.payment;
  if (existing && typeof existing === 'object') {
    // `<input type="date">` expects YYYY-MM-DD. Strip the time portion if the
    // queue.message carried a full ISO timestamp. We serialise back to a full
    // ISO datetime in `buildPaymentForApi` before sending.
    paymentDraft.value = { ...existing, date: isoToDateInput(existing.date) };
  } else {
    paymentDraft.value = null;
  }
}

/**
 * Convert the date input's YYYY-MM-DD value to the ISO 8601 datetime Odoo
 * requires (`%Y-%m-%dT%H:%M:%S%z`, e.g. 2026-06-09T15:11:57+02:00).
 * Uses current local time + the browser's timezone offset.
 */
function dateInputToIso(value) {
  if (!value) return '';
  // Already an ISO datetime — pass through.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
    return value;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!m) return String(value);
  const [, y, mo, d] = m;
  const now = new Date();
  const local = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds(),
  );
  const pad = (n) => String(n).padStart(2, '0');
  const tzMinutes = -local.getTimezoneOffset(); // > 0 east of UTC
  const sign = tzMinutes >= 0 ? '+' : '-';
  const absTz = Math.abs(tzMinutes);
  return (
    `${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}` +
    `T${pad(local.getHours())}:${pad(local.getMinutes())}:${pad(local.getSeconds())}` +
    `${sign}${pad(Math.floor(absTz / 60))}:${pad(absTz % 60)}`
  );
}

/** Extract the YYYY-MM-DD portion of an ISO datetime for `<input type="date">`. */
function isoToDateInput(value) {
  if (!value) return '';
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(value));
  return m ? m[1] : '';
}

function buildPaymentForApi(draft) {
  if (!draft || typeof draft !== 'object') return draft;
  return { ...draft, date: dateInputToIso(draft.date) };
}

async function onSubmit() {
  if (!canSubmit.value || !queueMessage.value) return;
  isSubmitting.value = true;
  submitError.value = '';
  try {
    const modifiedBy = newAuth?.currentUser?.uid ?? 'system';
    // operation_type is intentionally omitted — Odoo decides the right value
    // (CREATE_ORDER / UPDATE_ORDER / CORRECT_ORDER) based on the order's
    // current invoicing state. Forcing one from the client risks errors like
    // "Cannot update after invoicing. Use CORRECT_ORDER operation type instead."
    const payload = {
      format: queueMessage.value.format ?? true,
      model: queueMessage.value.model ?? 'sale.order',
      body: effectiveBody.value,
      modified_by: modifiedBy,
      modification_timestamp: new Date().toISOString(),
    };
    // Capture the queue.message id we saw before sending so the result step
    // knows what counts as "new" coming back from Odoo.
    previousQueueMessageId.value = queueMessage.value?.id ?? null;
    const { data } = await apiClient.rpIntegration.post(
      API_PATHS.rpIntegration.payOperations.orderAmendments.updateOdooPayment(uuid.value),
      payload,
    );
    submitSuccess.value = Boolean(data?.success);
    if (!submitSuccess.value) {
      submitError.value = data?.message || 'Backend reported a failure.';
    }
  } catch (err) {
    submitSuccess.value = false;
    submitError.value = err?.response?.data?.message ?? err?.message ?? 'Submit failed.';
  } finally {
    isSubmitting.value = false;
    phase.value = 'result';
    auditLogsCardRef.value?.refresh?.();
  }
}

function resetToLookup() {
  uuid.value = '';
  queueMessage.value = null;
  recovery.value = null;
  paymentDraft.value = null;
  acknowledgeOverwrite.value = false;
  submitError.value = '';
  submitSuccess.value = false;
  showJsonPreview.value = false;
  previousQueueMessageId.value = null;
  resetBodyDraft();
  phase.value = 'lookup';
}

async function copyUuid() {
  if (!uuid.value) return;
  const ok = await copyToClipboard(uuid.value);
  if (ok) toast.success('UUID copied!');
}
</script>

<style scoped>
.oa-workspace {
  padding: 24px;
  color: #182230;
  background: #f5f7fa;
}

.oa-workspace :deep(.oa-section-card) {
  margin-top: 32px;
  margin-bottom: 36px;
  overflow: visible;
  border: 1px solid #d0d5dd;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(16, 24, 40, 0.05);
}

.oa-workspace :deep(.oa-section-card > .card-header) {
  font-weight: 700;
}

.oa-workspace :deep(.oa-section-card > .card-body) {
  border-radius: 16px;
}

.oa-workspace :deep(.btn-primary),
.oa-workspace :deep(.btn-success),
.oa-workspace :deep(.btn-secondary) {
  color: #fff !important;
}

/* Intro text ------------------------------------------------------------- */
/* Override the global `.search-page p` rule that lowercases body copy and
   sets it to #a5a2a2 — we don't use <p> here so the page-wide rule misses,
   but if anything ever lands inside .oa-intro-body it should still be
   readable and case-preserved. */
.oa-intro-body {
  color: var(--bs-body-color, #212529);
  font-size: 0.95rem;
  line-height: 1.55;
  text-transform: none;
}

.oa-intro-body :deep(strong) {
  color: var(--bs-emphasis-color, #000);
}

.oa-intro-body :deep(code) {
  background: rgba(46, 52, 130, 0.07);
  color: var(--theme-default);
  padding: 0.05rem 0.35rem;
  border-radius: 0.25rem;
  font-size: 0.88em;
}

.oa-intro-alert {
  font-size: 0.9rem;
  color: var(--bs-body-color, #212529);
}

.oa-intro-alert :deep(strong) {
  color: var(--bs-emphasis-color, #000);
}

/* Stepper ---------------------------------------------------------------- */
.oa-stepper-card {
  background: linear-gradient(180deg, rgba(0, 161, 254, 0.04), transparent 70%);
}

.oa-stepper {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: stretch;
  gap: 0;
}

.oa-stepper__item {
  position: relative;
  padding: 0.25rem 0.5rem;
  display: grid;
  grid-template-rows: auto auto;
  row-gap: 0.55rem;
  text-align: center;
}

.oa-stepper__connector {
  position: absolute;
  top: 1.15rem;
  left: 0;
  right: 0;
  height: 4px;
  pointer-events: none;
}

.oa-stepper__item:first-child .oa-stepper__connector {
  left: 50%;
}

.oa-stepper__item:last-child .oa-stepper__connector {
  right: 50%;
}

.oa-stepper__connector-track {
  position: absolute;
  inset: 0;
  border-radius: 2px;
  background: rgba(15, 23, 42, 0.08);
}

.oa-stepper__connector-fill {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 0;
  border-radius: 2px;
  background: var(--theme-default);
  transition: width 0.45s ease;
}

.oa-stepper__item--done .oa-stepper__connector-fill {
  width: 100%;
}

.oa-stepper__item--current .oa-stepper__connector-fill {
  width: 50%;
}

.oa-stepper__marker {
  position: relative;
  display: inline-flex;
  width: 2.4rem;
  height: 2.4rem;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin: 0 auto;
  z-index: 1;
}

.oa-stepper__marker-bubble {
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 2px solid rgba(15, 23, 42, 0.12);
  color: rgba(15, 23, 42, 0.55);
  font-size: 0.95rem;
  transition:
    border-color 0.25s ease,
    background 0.25s ease,
    color 0.25s ease,
    transform 0.25s ease,
    box-shadow 0.25s ease;
}

.oa-stepper__item--current .oa-stepper__marker-bubble {
  border-color: var(--theme-default);
  background: var(--theme-default);
  color: #fff;
  transform: scale(1.05);
  box-shadow: 0 6px 18px rgba(0, 161, 254, 0.25);
  animation: oaStepperPulse 2.4s ease-in-out infinite;
}

.oa-stepper__item--done .oa-stepper__marker-bubble {
  border-color: var(--bs-success, #198754);
  background: var(--bs-success, #198754);
  color: #fff;
}

@keyframes oaStepperPulse {
  0%,
  100% {
    box-shadow: 0 6px 18px rgba(0, 161, 254, 0.25);
  }
  50% {
    box-shadow:
      0 6px 18px rgba(0, 161, 254, 0.25),
      0 0 0 8px rgba(0, 161, 254, 0.1);
  }
}

.oa-stepper__text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  line-height: 1.25;
}

.oa-stepper__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--bs-body-color, #212529);
  letter-spacing: 0.005em;
}

.oa-stepper__hint {
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--bs-secondary-color, #6c757d);
  max-width: 22ch;
}

.oa-stepper__item--current .oa-stepper__title {
  color: var(--theme-default);
}

.oa-stepper__item--current .oa-stepper__hint {
  color: var(--bs-body-color, #212529);
}

.oa-stepper__item--upcoming .oa-stepper__title {
  color: var(--bs-secondary-color, #6c757d);
}

.oa-stepper__item--done .oa-stepper__title {
  color: var(--bs-success, #198754);
}

@media (max-width: 575.98px) {
  .oa-stepper {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .oa-stepper__title {
    font-size: 0.78rem;
  }
  .oa-stepper__hint {
    display: none;
  }
  .oa-stepper__item--current .oa-stepper__hint {
    display: block;
  }
}

/* Context strip ---------------------------------------------------------- */
.oa-context-strip {
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: #fbfcff;
}

.oa-context-strip__label {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.66rem;
  font-weight: 700;
  color: var(--bs-secondary-color, #6c757d);
}

.oa-context-strip__uuid-value {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.85rem;
  color: var(--theme-default);
  background: rgba(46, 52, 130, 0.06);
  padding: 0.15rem 0.45rem;
  border-radius: 0.3rem;
  max-width: 26rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.oa-context-strip__copy {
  color: var(--bs-secondary-color, #6c757d);
  text-decoration: none;
}

.oa-context-strip__copy:hover {
  color: var(--theme-default);
}

.oa-context-strip__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.1);
  color: var(--bs-body-color);
  padding: 0.3rem 0.55rem;
  font-weight: 500;
  font-size: 0.78rem;
}

.oa-context-strip__chip-label {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.62rem;
  color: var(--bs-secondary-color, #6c757d);
  font-weight: 700;
}

.oa-context-strip__chip-value {
  color: var(--bs-body-color);
}

.oa-context-strip__state {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 0.7rem;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  border-radius: 999px;
}

.oa-context-strip__state--primary {
  background: rgba(0, 161, 254, 0.12);
  color: var(--theme-default);
}

.oa-context-strip__state--success {
  background: rgba(35, 183, 103, 0.15);
  color: #146c43;
}

.oa-context-strip__state--warning {
  background: rgba(214, 115, 0, 0.15);
  color: #8a4b00;
}

.oa-context-strip__state--danger {
  background: rgba(226, 36, 70, 0.13);
  color: #b02a37;
}

.oa-context-strip__state--secondary {
  background: rgba(108, 117, 125, 0.15);
  color: var(--bs-secondary-color, #6c757d);
}

.oa-context-strip__reset {
  color: var(--bs-secondary-color, #6c757d);
  text-decoration: none;
}

.oa-context-strip__reset:hover {
  color: var(--bs-danger, #dc3545);
}

/* JSON disclosure -------------------------------------------------------- */
/* Deliberately NOT a `.card.card-absolute` — the page-wide rule in
   `_fleet-operations.scss` makes every such card-header a floating navy tab,
   which would render this toggle dark-on-dark. We build the disclosure as a
   self-contained surface instead. */
.oa-json-disclosure {
  margin-bottom: 2rem;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 0.55rem;
  background: #fbfcff;
  overflow: hidden;
}

.oa-json-disclosure__toggle {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.85rem 1.1rem;
  background: transparent;
  border: 0;
  text-align: left;
  cursor: pointer;
  color: var(--bs-body-color, #212529);
  transition: background 0.15s ease;
}

.oa-json-disclosure__toggle:hover,
.oa-json-disclosure__toggle:focus-visible {
  background: rgba(0, 161, 254, 0.05);
  outline: none;
}

.oa-json-disclosure__icon {
  width: 1.85rem;
  height: 1.85rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.4rem;
  background: rgba(46, 52, 130, 0.07);
  color: var(--theme-default);
  font-size: 0.85rem;
  flex: 0 0 auto;
}

.oa-json-disclosure__label {
  font-weight: 600;
  color: var(--bs-body-color, #212529);
  font-size: 0.92rem;
}

.oa-json-disclosure__hint {
  color: var(--bs-secondary-color, #6c757d);
  font-size: 0.82rem;
}

.oa-json-disclosure__chev {
  margin-left: auto;
  color: var(--bs-secondary-color, #6c757d);
  transition: transform 0.15s ease;
}

.oa-json-disclosure__body {
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  background: #fff;
}

.oa-json-preview {
  border: none;
  border-radius: 0;
  resize: vertical;
  background: #fff;
  color: var(--bs-body-color, #212529);
}

/* Commit card ------------------------------------------------------------ */
.oa-commit-card {
  border: 1px solid rgba(35, 183, 103, 0.25);
  background: linear-gradient(180deg, rgba(35, 183, 103, 0.05), transparent);
}

.oa-commit-card--warn {
  border-color: rgba(214, 115, 0, 0.3);
  background: linear-gradient(180deg, rgba(214, 115, 0, 0.06), transparent);
}

.oa-commit-card__warning {
  display: flex;
  gap: 0.8rem;
  align-items: flex-start;
  padding: 0.85rem 1rem;
  border-radius: 0.5rem;
  background: rgba(214, 115, 0, 0.1);
  color: #8a4b00;
  border: 1px solid rgba(214, 115, 0, 0.22);
}

.oa-commit-card__warning .fa {
  font-size: 1.1rem;
  margin-top: 0.15rem;
}

.oa-commit-card__send {
  min-width: 14rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.oa-commit-card__send:not(:disabled) {
  box-shadow: 0 6px 18px rgba(35, 183, 103, 0.25);
}
</style>
